import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { loadVariablePatterns } from './utils/templateVariables';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import IdentitiesPage from './components/IdentitiesPage';
import ChannelsPage from './components/ChannelsPage';
import GatewaysPage from './components/GatewaysPage';
import MediatorsPage from './components/MediatorsPage';
import IntegrationsPage from './components/IntegrationsPage';
import TrustRegistriesPage from './components/TrustRegistriesPage';
import UsersPage from './components/UsersPage';
import NotificationsPage from './components/NotificationsPage';
import TasksPage from './components/TasksPage';
import SettingsPage from './components/SettingsPage';
import LogsPage from './components/LogsPage';
import EditChannelPage from './components/EditChannelPage';
import EditGatewayPage from './components/EditGatewayPage';
import EditConnectionPointPage from './components/EditConnectionPointPage';
import CreateGatewayConnectionPointWizardPage from './components/CreateGatewayConnectionPointWizardPage';
import AddGatewayViaOOBWizardPage from './components/AddGatewayViaOOBWizardPage';
import ApproveGatewayWizardPage from './components/ApproveGatewayWizardPage';
import EditMediatorPage from './components/EditMediatorPage';
import EditMcpProxyPage from './components/EditMcpProxyPage';
import AddMcpProxyWizardPage from './components/AddMcpProxyWizardPage';
import EditTrustRegistryPage from './components/EditTrustRegistryPage';
import EditUserPage from './components/EditUserPage';
import UserIntegrationsPage from './components/UserIntegrationsPage';
import IdentityIntegrationsPage from './components/IdentityIntegrationsPage';
import ProfilePage from './components/ProfilePage';
import EditNotificationPage from './components/EditNotificationPage';
import ChannelCapturePage from './components/ChannelCapturePage';
import OnboardWizardPage from './components/OnboardWizardPage';
import AddChannelWizardPage from './components/AddChannelWizardPage';
import AddMpxChannelWizardPage from './components/AddMpxChannelWizardPage';
import AddMediatorWizardPage from './components/AddMediatorWizardPage';
import MpxTenantsPage from './components/MpxTenantsPage';
import AddMpxTenantWizardPage from './components/AddMpxTenantWizardPage';
import EditMpxTenantPage from './components/EditMpxTenantPage';
import AddTrustRegistryWizardPage from './components/AddTrustRegistryWizardPage';
import AddIntegrationWizardPage from './components/AddIntegrationWizardPage';
import EditIntegrationPage from './components/EditIntegrationPage';
import MetricsPage from './pages/MetricsPage';
import SecretsPage from './pages/SecretsPage';
import EditSecretPage from './components/EditSecretPage';
import PipesPage from './pages/PipesPage';
import AddPipeWizardPage from './components/AddPipeWizardPage';
import EditPipePage from './pages/EditPipePage';
import PipeMetricsPage from './pages/PipeMetricsPage';
import LoginPage from './pages/LoginPage';

// Import the CSS
import './dashboard.css';

const AppContent: React.FC = () => {
  const { state, actions } = useApp();
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [sessionToken, setSessionToken] = React.useState<string | null>(null);
  const hasRefreshedCache = React.useRef<boolean>(false);

  // Load variable patterns from config on mount
  React.useEffect(() => {
    loadVariablePatterns().catch(err => {
      console.warn('Failed to load variable patterns:', err);
    });
  }, []);

  // Add/remove dashboard class based on auth state
  React.useEffect(() => {
    if (isAuthenticated) {
      document.body.classList.add('dashboard-mode');
    } else {
      document.body.classList.remove('dashboard-mode');
    }
  }, [isAuthenticated]);

  // Check for existing session on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      const token = sessionStorage.getItem('session_token');
      
      try {
        const response = await fetch(`/api/auth/check?session_token=${token || ''}`);
        
        if (response.status === 404) {
          // Auth endpoint doesn't exist - passkey auth is disabled
          setIsAuthenticated(true);
          setIsLoading(false);
          // Connect websocket for unauthenticated mode (non-blocking)
          setTimeout(() => {
            actions.connectWebSocket();
          }, 0);
        } else if (response.ok) {
          const data = await response.json();
          const authenticated = data.authenticated === true;
          setIsAuthenticated(authenticated);
          setIsLoading(false);
          if (authenticated && token) {
            setSessionToken(token);
            // Connect websocket after successful auth check (non-blocking)
            setTimeout(() => {
              actions.connectWebSocket();
            }, 0);
          }
        } else {
          // Auth endpoint exists but returned error - require login
          console.warn('Authentication check failed with status:', response.status);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        // Network error - fail secure, require login
        console.error('Authentication check failed:', error);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };
    
    checkAuth().catch(err => {
      console.error('AppContent: Unexpected error in checkAuth:', err);
      setIsAuthenticated(false);
      setIsLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh gateway channel cache after authentication
  React.useEffect(() => {
    if (isAuthenticated && !hasRefreshedCache.current) {
      hasRefreshedCache.current = true;
      fetch('/api/v1/gateways/refresh-channels', {
        method: 'POST',
      })
        .then(response => response.json())
        .catch(error => {
          console.warn('Failed to refresh gateway channel cache:', error);
        });
    }
  }, [isAuthenticated]);

  const handleLoginSuccess = (token: string) => {
    sessionStorage.setItem('session_token', token);
    setSessionToken(token);
    setIsAuthenticated(true);
    actions.connectWebSocket();
    // Reload will realise that we're authenticated and render the dashboard
    window.location.reload();
  };

  const handleLogout = async () => {
    // Disconnect websocket before logout
    actions.disconnectWebSocket();
    
    if (sessionToken) {
      try {
        await fetch(`/api/auth/logout?session_token=${sessionToken}`, {
          method: 'POST',
        });
      } catch (error) {
        console.error('Logout failed:', error);
      }
    }
    sessionStorage.removeItem('session_token');
    setSessionToken(null);
    setIsAuthenticated(false);
  };

  // Add body class for sidebar state
  React.useEffect(() => {
    if (state.sidebarCollapsed) {
      document.body.classList.add('sidebar-toggled');
    } else {
      document.body.classList.remove('sidebar-toggled');
    }
  }, [state.sidebarCollapsed]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#667eea'
      }}>
        <div>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className={`${state.theme === 'dark' ? 'dark-theme' : ''}`}>
      {/* Scroll to top on route change */}
      <ScrollToTop />
      
      {/* Page Wrapper */}
      <div id="wrapper">
        {/* Sidebar */}
        <Sidebar />
        
        {/* Content Wrapper */}
        <div id="content-wrapper" className="d-flex flex-column">
          {/* Main Content */}
          <div id="content">
            {/* Topbar */}
            <Header onLogout={handleLogout} />
            
            {/* Begin Page Content */}
            <Routes>
              <Route path="/" element={<ProtectedRoute permission="dashboard.view"><Dashboard /></ProtectedRoute>} />
              <Route path="/metrics" element={<ProtectedRoute permission="metrics.view"><MetricsPage /></ProtectedRoute>} />
              <Route path="/onboard" element={<OnboardWizardPage />} />
              <Route path="/identities" element={<IdentitiesPage />} />
              <Route path="/identities/:did" element={<IdentitiesPage />} />
              <Route path="/identities/integrations" element={<IdentityIntegrationsPage />} />
              <Route path="/channels" element={<ProtectedRoute permission="channels.view"><ChannelsPage /></ProtectedRoute>} />
              <Route path="/channels/view/:configId" element={<ProtectedRoute permission="channels.view"><ChannelsPage /></ProtectedRoute>} />
              <Route path="/channels/new" element={<ProtectedRoute permission="channels.edit"><AddChannelWizardPage /></ProtectedRoute>} />
              <Route path="/channels/new/mpx" element={<ProtectedRoute permission="channels.edit"><AddMpxChannelWizardPage /></ProtectedRoute>} />
              <Route path="/channels/:configId" element={<ProtectedRoute permission="channels.edit"><EditChannelPage /></ProtectedRoute>} />
              <Route path="/channels/:configId/capture" element={<ProtectedRoute permission="channels.capture"><ChannelCapturePage /></ProtectedRoute>} />
              <Route path="/gateways" element={<ProtectedRoute permission="gateways.view"><GatewaysPage /></ProtectedRoute>} />
              <Route path="/gateways/view/:id" element={<ProtectedRoute permission="gateways.view"><GatewaysPage /></ProtectedRoute>} />
              <Route path="/gateways/publish" element={<ProtectedRoute permission="gateways.edit"><CreateGatewayConnectionPointWizardPage /></ProtectedRoute>} />
              <Route path="/gateways/connect" element={<ProtectedRoute permission="gateways.edit"><AddGatewayViaOOBWizardPage /></ProtectedRoute>} />
              <Route path="/gateways/:id/approve" element={<ProtectedRoute permission="gateways.edit"><ApproveGatewayWizardPage /></ProtectedRoute>} />
              <Route path="/gateways/new" element={<ProtectedRoute permission="gateways.edit"><EditGatewayPage /></ProtectedRoute>} />
              <Route path="/gateways/:id" element={<ProtectedRoute permission="gateways.edit"><EditGatewayPage /></ProtectedRoute>} />
              <Route path="/connection-points/:id" element={<ProtectedRoute permission="gateways.edit"><EditConnectionPointPage /></ProtectedRoute>} />
              <Route path="/mediators" element={<ProtectedRoute permission="mediators.view"><MediatorsPage /></ProtectedRoute>} />
              <Route path="/mediators/view/:id" element={<ProtectedRoute permission="mediators.view"><MediatorsPage /></ProtectedRoute>} />
              <Route path="/mediators/wizard" element={<ProtectedRoute permission="mediators.edit"><AddMediatorWizardPage /></ProtectedRoute>} />
              <Route path="/mediators/new" element={<ProtectedRoute permission="mediators.edit"><EditMediatorPage /></ProtectedRoute>} />
              <Route path="/mediators/:id" element={<ProtectedRoute permission="mediators.edit"><EditMediatorPage /></ProtectedRoute>} />
              <Route path="/mpx-tenants" element={<ProtectedRoute permission="mpx_tenants.view"><MpxTenantsPage /></ProtectedRoute>} />
              <Route path="/mpx-tenants/wizard" element={<ProtectedRoute permission="mpx_tenants.edit"><AddMpxTenantWizardPage /></ProtectedRoute>} />
              <Route path="/mpx-tenants/edit/:id" element={<ProtectedRoute permission="mpx_tenants.edit"><EditMpxTenantPage /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute permission="mcp_proxies.view"><IntegrationsPage /></ProtectedRoute>} />
              <Route path="/integrations/mcp-proxies/wizard" element={<ProtectedRoute permission="mcp_proxies.edit"><AddMcpProxyWizardPage /></ProtectedRoute>} />
              <Route path="/integrations/mcp-proxies/:id" element={<ProtectedRoute permission="mcp_proxies.edit"><EditMcpProxyPage /></ProtectedRoute>} />
              <Route path="/integrations/integrations/wizard" element={<ProtectedRoute permission="mcp_proxies.edit"><AddIntegrationWizardPage /></ProtectedRoute>} />
              <Route path="/integrations/integrations/:id" element={<ProtectedRoute permission="mcp_proxies.edit"><EditIntegrationPage /></ProtectedRoute>} />
              <Route path="/trust-registries" element={<ProtectedRoute permission="trust_registries.view"><TrustRegistriesPage /></ProtectedRoute>} />
              <Route path="/trust-registries/wizard" element={<ProtectedRoute permission="trust_registries.edit"><AddTrustRegistryWizardPage /></ProtectedRoute>} />
              <Route path="/trust-registries/new" element={<ProtectedRoute permission="trust_registries.edit"><EditTrustRegistryPage /></ProtectedRoute>} />
              <Route path="/trust-registries/:id" element={<ProtectedRoute permission="trust_registries.edit"><EditTrustRegistryPage /></ProtectedRoute>} />
              <Route path="/pipes" element={<ProtectedRoute permission="pipes.view"><PipesPage /></ProtectedRoute>} />
              <Route path="/pipes/new" element={<ProtectedRoute permission="pipes.edit"><AddPipeWizardPage /></ProtectedRoute>} />
              <Route path="/pipes/:pipeId" element={<ProtectedRoute permission="pipes.edit"><EditPipePage /></ProtectedRoute>} />
              <Route path="/pipes/metrics" element={<ProtectedRoute permission="pipes.view"><PipeMetricsPage /></ProtectedRoute>} />
              <Route path="/pipes/mcp-proxies/wizard" element={<ProtectedRoute permission="mcp_proxies.edit"><AddMcpProxyWizardPage /></ProtectedRoute>} />
              <Route path="/pipes/mcp-proxies/:id" element={<ProtectedRoute permission="mcp_proxies.edit"><EditMcpProxyPage /></ProtectedRoute>} />
              <Route path="/secrets" element={<ProtectedRoute permission="secrets.view"><SecretsPage /></ProtectedRoute>} />
              <Route path="/secrets/new" element={<ProtectedRoute permission="secrets.edit"><EditSecretPage /></ProtectedRoute>} />
              <Route path="/secrets/:id" element={<ProtectedRoute permission="secrets.edit"><EditSecretPage /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute permission="users.view"><UsersPage /></ProtectedRoute>} />
              <Route path="/users/integrations" element={<ProtectedRoute permission="users.edit"><UserIntegrationsPage /></ProtectedRoute>} />
              <Route path="/users/:userId" element={<ProtectedRoute permission="users.edit"><EditUserPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/notifications" element={<ProtectedRoute permission="notifications.view"><NotificationsPage /></ProtectedRoute>} />
              <Route path="/notifications/new" element={<ProtectedRoute permission="notifications.edit"><EditNotificationPage /></ProtectedRoute>} />
              <Route path="/notifications/:id" element={<ProtectedRoute permission="notifications.edit"><EditNotificationPage /></ProtectedRoute>} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/settings" element={<ProtectedRoute permission="settings.view"><SettingsPage /></ProtectedRoute>} />
              <Route path="/logs" element={<ProtectedRoute permission="logs.view"><LogsPage /></ProtectedRoute>} />
            </Routes>
          </div>
          
          {/* Footer */}
          <footer className="sticky-footer bg-white">
            <div className="container my-auto">
              <div className="copyright text-center my-auto">
                <span>Copyright © Affinidi Pte Ltd 2026</span>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {/* Scroll to Top Button*/}
      <button 
        className="scroll-to-top rounded"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        style={{ 
          display: 'none' // Will be shown/hidden by scroll handler if implemented
        }}
        title="Scroll to top"
      >
        <i className="fas fa-angle-up"></i>
      </button>

      {/* Error Display */}
      {state.error && (
        <div 
          className="position-fixed alert alert-danger alert-dismissible fade show"
          style={{ 
            top: '20px', 
            right: '20px', 
            zIndex: 1055,
            maxWidth: '400px'
          }}
        >
          <strong>Error!</strong> {state.error}
          <button type="button" className="close">
            <span>&times;</span>
          </button>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppProvider>
        <PermissionsProvider>
          <AppContent />
        </PermissionsProvider>
      </AppProvider>
    </Router>
  );
};

export default App;
